import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  MOTION_STORAGE_KEY,
  REDUCED_MOTION_QUERY,
  type MotionPreference,
  type ResolvedMotion,
  readMotionPreference,
  resolveMotionPreference,
  writeMotionPreference,
} from "./motion";

type MotionContextValue = {
  preference: MotionPreference;
  resolved: ResolvedMotion;
  systemReduced: boolean;
  setPreference: (value: MotionPreference) => void;
};

const MotionContext = createContext<MotionContextValue | null>(null);

const mediaQuery = (): MediaQueryList | undefined => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return undefined;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY);
};

export const MotionProvider = ({ children }: PropsWithChildren) => {
  const query = mediaQuery();
  const [preference, setPreferenceState] = useState<MotionPreference>(() =>
    readMotionPreference(
      typeof window === "undefined" ? undefined : window.localStorage,
    ),
  );
  const [systemReduced, setSystemReduced] = useState(query?.matches ?? false);
  const resolved = resolveMotionPreference(preference, systemReduced);

  useEffect(() => {
    const currentQuery = mediaQuery();
    if (!currentQuery) {
      return undefined;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemReduced(event.matches);
    };
    setSystemReduced(currentQuery.matches);
    currentQuery.addEventListener("change", handleChange);
    return () => currentQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.motionPreference = preference;
    document.documentElement.dataset.motionResolved = resolved;
  }, [preference, resolved]);

  const setPreference = (value: MotionPreference) => {
    setPreferenceState(value);
    writeMotionPreference(window.localStorage, value);
  };

  const contextValue = useMemo<MotionContextValue>(
    () => ({
      preference,
      resolved,
      systemReduced,
      setPreference,
    }),
    [preference, resolved, systemReduced],
  );

  return (
    <MotionContext.Provider value={contextValue}>
      {children}
    </MotionContext.Provider>
  );
};

export const useMotion = (): MotionContextValue => {
  const value = useContext(MotionContext);
  if (!value) {
    throw new Error("useMotion must be used inside MotionProvider");
  }
  return value;
};

export { MOTION_STORAGE_KEY };
